import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useDaycareCapacity } from "@/hooks/useDaycareCapacity";
import { useHotelRooms } from "@/hooks/useHotelRooms";
import { findCapacityViolations, type CapacityReservation } from "./reservationCapacity";

export function useReservationCapacityCheck(args: {
  module: "daycare" | "hotel";
  date: string;
  checkOutDate: string | null;
  petId: string | null;
  roomId: string | null;
  bedId: string | null;
}) {
  const { data: profile } = useProfile();
  const daycare = useDaycareCapacity();
  const rooms = useHotelRooms();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: ["reservation-capacity-check", clinicId, args],
    enabled: !!clinicId && !!args.petId && !!args.date,
    queryFn: async () => {
      const [petResult, dayResult] = await Promise.all([
        supabase.from("pets").select("size_category_id, pet_size_categories(name)").eq("id", args.petId!).maybeSingle(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from("stay_reservation_days")
          .select(
            "stay_date, hotel_room_id, hotel_room_bed_id, stay_reservations!inner(module_type), pets(size_category_id, pet_size_categories(name))",
          )
          .eq("clinic_id", clinicId!)
          .in("status", ["scheduled", "checked_in"])
          .eq("stay_date", args.date),
      ]);
      if (petResult.error) throw petResult.error;
      if (dayResult.error) throw dayResult.error;
      const sizeName = (petResult.data?.pet_size_categories as { name?: string } | null)?.name?.toLowerCase() ?? "";
      const size = sizeName.includes("small") || sizeName.includes("pequeno") ? "small" : sizeName.includes("large") || sizeName.includes("grande") ? "large" : sizeName ? "medium" : null;
      const existing: CapacityReservation[] = ((dayResult.data ?? []) as Array<{
        stay_date: string;
        hotel_room_id: string | null;
        hotel_room_bed_id: string | null;
        stay_reservations: { module_type: string } | null;
        pets: { pet_size_categories?: { name?: string } | null } | null;
      }>).map((row) => {
        const petSize = row.pets?.pet_size_categories?.name?.toLowerCase() ?? "";
        const moduleType = row.stay_reservations?.module_type;
        return {
          moduleType: moduleType === "daycare" ? "daycare" : moduleType === "hotel" ? "hotel" : "both",
          stayDate: row.stay_date,
          size: petSize.includes("small") || petSize.includes("pequeno") ? "small" : petSize.includes("large") || petSize.includes("grande") ? "large" : petSize ? "medium" : null,
          roomId: row.hotel_room_id,
          bedId: row.hotel_room_bed_id,
        };
      });
      const roomLimits = Object.fromEntries((rooms.data ?? []).map((room) => [room.id, { label: room.name, limit: room.capacity ?? room.beds.length }]));
      const bedLimits = Object.fromEntries((rooms.data ?? []).flatMap((room) => room.beds.map((bed) => [bed.id, { label: `${room.name} · ${bed.name}`, limit: 1 }] as const)));
      return findCapacityViolations({ module: args.module, dates: [args.date], size, roomId: args.roomId, bedId: args.bedId, existing, limits: { day: args.module === "daycare" ? daycare.data?.totalCapacity ?? null : null, limitBySize: daycare.data?.limitBySize ?? false, bySize: { small: daycare.data?.capacitySmall, medium: daycare.data?.capacityMedium, large: daycare.data?.capacityLarge }, room: roomLimits, bed: bedLimits } });
    },
  });
}
